/**
 * ApprovalsPage — approval queue and budget-ceiling controls.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05C. Approve/deny are POSTs
 * the server owns; this page only relays them and re-renders the response,
 * and every decision requires a non-empty reason — the client enforces that
 * too (`api/operations.ts` rejects a blank reason with code
 * `reason_required`), matching the audit posture C_OPS established for
 * operator actions.
 *
 * Ceiling controls: each row shows server-reported `spent_usd` beside
 * `ceiling_usd` and accepts an update only with a reason. No figure here is
 * computed client-side, and showing a ceiling never grants anything —
 * enforcement stays in the spend-policy backend, never in this page.
 *
 * UNAVAILABLE IS HONEST: `/v1/operations/approvals` and `/ceilings` are
 * documented contracts, not mounted at this base ref; a 404/503 renders an
 * explicit Unavailable state — never a fabricated queue.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import {
  OperationsApiError,
  approveRequest,
  denyRequest,
  fetchApprovals,
  fetchCeilings,
  updateCeiling,
} from '../../api/operations';

function ApprovalsPage() {
  const [approvals, setApprovals] = useState(null);
  const [ceilings, setCeilings] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');
  const [reason, setReason] = useState('');
  const [ceilingEdits, setCeilingEdits] = useState({});
  const [ceilingReasons, setCeilingReasons] = useState({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await fetchApprovals();
      setApprovals(response.approvals || []);
    } catch (err) {
      if (err instanceof OperationsApiError && err.isUnavailable) {
        setApprovals([]);
        setError('unavailable');
      } else {
        setApprovals([]);
        setError(err instanceof Error ? err.message : 'the approval queue could not be loaded');
      }
    }
    try {
      const response = await fetchCeilings();
      setCeilings(response.ceilings || []);
    } catch {
      // A ceiling fetch failure must not hide the approval queue; the
      // section below renders its own empty state.
      setCeilings([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = useCallback(
    async (approvalId, verb) => {
      setBusy(approvalId + ':' + verb);
      try {
        if (verb === 'approve') await approveRequest(approvalId, { reason });
        else await denyRequest(approvalId, { reason });
        setReason('');
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'the decision failed');
      } finally {
        setBusy('');
      }
    },
    [reason, load],
  );

  const saveCeiling = useCallback(
    async (scope) => {
      const next = ceilingEdits[scope];
      const why = ceilingReasons[scope] || '';
      if (!next || !why.trim()) return;
      setBusy('ceiling:' + scope);
      try {
        await updateCeiling(scope, { ceiling_usd: next, reason: why });
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'the ceiling update failed');
      } finally {
        setBusy('');
      }
    },
    [ceilingEdits, ceilingReasons, load],
  );

  if (error === 'unavailable') {
    return (
      <section>
        <h1>Approvals</h1>
        <p className="unavailable">
          Unavailable — the operations backend is not mounted for this deployment, so no
          approval can be listed. Nothing here is fabricated.
        </p>
      </section>
    );
  }

  if (approvals === null) {
    return (
      <section>
        <h1>Approvals</h1>
        <p>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      <h1>Approvals</h1>
      {error && error !== 'unavailable' ? <p className="error">{error}</p> : null}
      <label>
        Reason (required for every decision)
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      {approvals.length === 0 ? (
        <p>The server reported no pending approvals.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Approval</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Requested by</th>
              <th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {approvals.map((approval) => (
              <tr key={approval.approval_id}>
                <td>{approval.approval_id}</td>
                <td>{approval.subject}</td>
                <td><span className="badge">{approval.status}</span></td>
                <td>{approval.requested_by}</td>
                <td>
                  <button
                    type="button"
                    disabled={busy !== '' || !reason.trim()}
                    onClick={() => decide(approval.approval_id, 'approve')}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={busy !== '' || !reason.trim()}
                    onClick={() => decide(approval.approval_id, 'deny')}
                  >
                    Deny
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Budget ceilings</h2>
      <p className="note">
        Figures are server-reported and rendered verbatim; displaying a ceiling never
        grants anything — spend enforcement stays in the backend policy, never here.
      </p>
      {ceilings.length === 0 ? (
        <p>No ceilings were reported by the server.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Scope</th>
              <th>Spent</th>
              <th>Ceiling</th>
              <th>New ceiling (with reason)</th>
            </tr>
          </thead>
          <tbody>
            {ceilings.map((ceiling) => (
              <tr key={ceiling.scope}>
                <td>{ceiling.scope}</td>
                <td className="mono">{ceiling.spent_usd}</td>
                <td className="mono">{ceiling.ceiling_usd}</td>
                <td>
                  <input
                    type="text"
                    placeholder={ceiling.ceiling_usd}
                    value={ceilingEdits[ceiling.scope] || ''}
                    onChange={(e) =>
                      setCeilingEdits({ ...ceilingEdits, [ceiling.scope]: e.target.value })
                    }
                  />
                  <input
                    type="text"
                    placeholder="reason"
                    value={ceilingReasons[ceiling.scope] || ''}
                    onChange={(e) =>
                      setCeilingReasons({ ...ceilingReasons, [ceiling.scope]: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    disabled={
                      busy !== '' ||
                      !(ceilingEdits[ceiling.scope] || '').trim() ||
                      !(ceilingReasons[ceiling.scope] || '').trim()
                    }
                    onClick={() => saveCeiling(ceiling.scope)}
                  >
                    Update
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default ApprovalsPage;
